import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { BookingStep } from '@link/share/booking';
import {
  CalendarSkeleton,
  UnitSkeleton
} from '@link/share/business/ProductPage/Skeleton';
import { useBookingStore } from '../context';
import { PlanCalendar } from './PlanCalendar';
import { PlanPicker } from './PlanPicker';
import { PlanUnits } from './PlanUnits';

interface Props {
  onLoadMore: () => void;
}

/**
 * The column layout: one calendar for every plan, then the plans side by
 * side, then the units — three steps down the page.
 *
 * The plan step renders on the server from the product. The calendar and the
 * units come from the calendar API, so the server and the first browser
 * render show skeletons for those two.
 */
export const PlanSteps = observer(function PlanSteps({ onLoadMore }: Props) {
  const store = useBookingStore();
  const { t } = useTranslation();
  const activePlan = store.collapsiblePlans.find(
    plan => plan.activityPlan.id === store.activePlanId
  );
  const isLoading = store.calendar.isLoading;
  return (
    <>
      {activePlan?.showsCalendarDate && (
        <BookingStep title={t('triplabo.product.panels.selectDate')}>
          {isLoading ? (
            <CalendarSkeleton />
          ) : (
            <PlanCalendar
              planId={store.activePlanId ?? ''}
              isDayBookable={store.isDayBookableForAnyPlan}
              framed
              onLoadMore={onLoadMore}
            />
          )}
        </BookingStep>
      )}
      <BookingStep title={t('triplabo.product.panels.selectPlan')}>
        <PlanPicker />
      </BookingStep>
      <BookingStep title={t('triplabo.product.panels.selectPeople')}>
        {isLoading ? (
          <UnitSkeleton />
        ) : (
          activePlan && <PlanUnits plan={activePlan} />
        )}
      </BookingStep>
    </>
  );
});
